import type { FC } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { Text, TouchableRipple } from 'react-native-paper';

import { DownIcon, LocationIcon, SearchIcon } from '@/assets';
import { useGetPerson } from '@/features/api/hooks/participant/useGetPerson';
import { useGetGeoArea } from '@/features/api/hooks/participant-geo-area/useGetGeoArea';
import { useGetSelectedGeoAreas } from '@/features/api/hooks/participant-geo-area/useGetSelectedGeoAreas';
import { useFeature } from '@/features/feature-management/provider/useFeature';
import { FeaturesEnum } from '@/features/feature-management/types/features';
import { HomeAreaChangeSheet } from '@/features/geo-area/components/subscription/HomeAreaChangeSheet';
import { PostsRoute } from '@/features/posts/navigation/postsRoute';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { useAppNavigation } from '@/navigation/hooks/useAppNavigation';
import { hasItems } from '@/shared/utils/array';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';

export const MEIN_ORT_LOCATION_BAR_HEIGHT = 60;

const makeStyles = (theme: AppTheme) =>
  StyleSheet.create({
    bar: {
      height: MEIN_ORT_LOCATION_BAR_HEIGHT,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: 12,
      backgroundColor: theme.colors.surface
    },
    locTouch: {
      flexShrink: 1,
      borderRadius: 12,
      paddingVertical: 2,
      paddingHorizontal: 4
    },
    locHover: { backgroundColor: theme.stateLayers.hover },
    loc: {
      flexShrink: 1,
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6
    },
    nameRow: {
      flexShrink: 1,
      flexDirection: 'row',
      alignItems: 'center',
      gap: 2
    },
    name: {
      fontFamily: 'Montserrat_500Medium',
      fontSize: 22,
      lineHeight: 28,
      color: theme.colors.onSurface,
      flexShrink: 1
    },
    chevron: { transform: [{ translateY: 2 }] },
    locTextContainer: { flexShrink: 1 },
    sub: {
      fontSize: 14,
      lineHeight: 20,
      fontWeight: '500',
      letterSpacing: 0.25,
      marginTop: 2,
      color: theme.colors.onSurfaceVariant
    },
    search: {
      width: 40,
      height: 40,
      borderRadius: 20,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'transparent'
    },
    searchHover: { backgroundColor: theme.stateLayers.hover }
  });

export const MeinOrtLocationBar: FC = () => {
  const theme = useAppTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);
  const navigation = useAppNavigation();
  const [searchHover, setSearchHover] = useState(false);
  const [locHover, setLocHover] = useState(false);
  const [homeAreaSheetVisible, setHomeAreaSheetVisible] = useState(false);

  const { isFeatureEnabled } = useFeature();
  const isGeoAreaSelectionEnabled = isFeatureEnabled(
    FeaturesEnum.GeoAreaSelection
  );

  const { data: person } = useGetPerson();
  const { data: homeGeoArea } = useGetGeoArea(person?.homeAreaId);
  const { data: selectedGeoAreas } = useGetSelectedGeoAreas();

  const { t } = useTranslation(LocalizationNameSpace.START, {
    keyPrefix: 'screens.start.sections.header'
  });

  const nearGeoAreas = useMemo(
    () =>
      selectedGeoAreas?.filter(
        ({ id, leaf }) => leaf === true && id !== homeGeoArea?.id
      ) ?? [],
    [selectedGeoAreas, homeGeoArea?.id]
  );

  const onPress = useCallback(() => {
    setHomeAreaSheetVisible(true);
  }, []);

  const onHoverIn = useCallback(() => {
    setLocHover(true);
  }, []);

  const onHoverOut = useCallback(() => {
    setLocHover(false);
  }, []);

  return (
    <View style={styles.bar}>
      <TouchableRipple
        testID="mein-ort-location-button"
        onPress={onPress}
        onHoverIn={onHoverIn}
        onHoverOut={onHoverOut}
        rippleColor={theme.rippleColors.onSurface}
        borderless
        style={[styles.locTouch, locHover && styles.locHover]}
      >
        <View style={styles.loc}>
          <LocationIcon fill={theme.colors.onSurface} width={24} height={24} />
          <View style={styles.locTextContainer}>
            <View style={styles.nameRow}>
              <Text style={styles.name} numberOfLines={1} ellipsizeMode="tail">
                {homeGeoArea?.name}
              </Text>
              {isGeoAreaSelectionEnabled && (
                <View style={styles.chevron}>
                  <DownIcon
                    fill={theme.colors.onSurface}
                    width={24}
                    height={24}
                  />
                </View>
              )}
            </View>
            {hasItems(nearGeoAreas) && (
              <Text style={styles.sub} numberOfLines={1} ellipsizeMode="tail">
                {
                  // TODO(scrum-23): replace with the radius label ("Umkreis X km")
                  t('subscribedGeoAreas', { count: nearGeoAreas.length })
                }
              </Text>
            )}
          </View>
        </View>
      </TouchableRipple>

      <TouchableRipple
        testID="mein-ort-search-button"
        onPress={() => navigation.navigate(PostsRoute.PostSearch)}
        onHoverIn={() => setSearchHover(true)}
        onHoverOut={() => setSearchHover(false)}
        rippleColor={theme.rippleColors.onSurface}
        borderless
        style={[styles.search, searchHover && styles.searchHover]}
      >
        <SearchIcon fill={theme.colors.onSurface} width={24} height={24} />
      </TouchableRipple>
      <HomeAreaChangeSheet
        visible={homeAreaSheetVisible}
        onDismiss={() => setHomeAreaSheetVisible(false)}
      />
    </View>
  );
};
